// Broker constants + helpers (Round 92o++++++++N, Feb 2026).
//
// Single source of truth for broker labels and tone palettes on the
// frontend. Mirrors the backend BROKERS tuple in server.py — keep them
// in lockstep. The list intentionally covers the most-asked-for
// brokerages first; "Other" is the safety net so no user feels
// excluded.

import {
  Banknote, Building2, BarChart3, Rocket, TrendingUp, Sparkles,
  Landmark, BookOpen, HandCoins, ShieldCheck, Coins, HelpCircle,
} from 'lucide-react';

// `null` / missing → "Unset" (no broker tag). Same conservative
// default the backend uses; lets users adopt brokers gradually
// without forcing a value on every legacy lot.
export const BROKER_UNSET = '';

export const BROKERS = [
  { key: 'schwab',       label: 'Charles Schwab',       short: 'Schwab',    icon: Landmark },
  { key: 'fidelity',     label: 'Fidelity',              short: 'Fidelity',  icon: ShieldCheck },
  { key: 'vanguard',     label: 'Vanguard',              short: 'Vanguard',  icon: Building2 },
  { key: 'robinhood',    label: 'Robinhood',             short: 'Robinhood', icon: Rocket },
  { key: 'm1',           label: 'M1 Finance',            short: 'M1',        icon: TrendingUp },
  { key: 'webull',       label: 'Webull',                short: 'Webull',    icon: Sparkles },
  { key: 'ibkr',         label: 'Interactive Brokers',   short: 'IBKR',      icon: BarChart3 },
  { key: 'etrade',       label: 'E*TRADE',               short: 'E*TRADE',   icon: Banknote },
  { key: 'merrill',      label: 'Merrill Edge',          short: 'Merrill',   icon: BookOpen },
  { key: 'sofi',         label: 'SoFi Invest',           short: 'SoFi',      icon: HandCoins },
  { key: 'public',       label: 'Public.com',            short: 'Public',    icon: Coins },
  { key: 'jpm',          label: 'JPM Self-Directed',     short: 'JPM',       icon: Landmark },
  { key: 'other',        label: 'Other',                 short: 'Other',     icon: HelpCircle },
];

export const BROKER_KEYS = BROKERS.map((b) => b.key);

export function getBroker(key) {
  if (!key) return null;
  return BROKERS.find((b) => b.key === key) || null;
}

// Tone classes for broker chips. Each broker gets a distinct palette
// so a portfolio holding lots across 4 brokers reads at a glance.
export const BROKER_TONE = {
  schwab:    'bg-blue-50 text-blue-700 ring-blue-300 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-700/50',
  fidelity:  'bg-green-50 text-green-700 ring-green-300 dark:bg-green-950/40 dark:text-green-200 dark:ring-green-700/50',
  vanguard:  'bg-red-50 text-red-700 ring-red-300 dark:bg-red-950/40 dark:text-red-200 dark:ring-red-700/50',
  robinhood: 'bg-lime-50 text-lime-700 ring-lime-300 dark:bg-lime-950/40 dark:text-lime-200 dark:ring-lime-700/50',
  m1:        'bg-amber-50 text-amber-700 ring-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-700/50',
  webull:    'bg-orange-50 text-orange-700 ring-orange-300 dark:bg-orange-950/40 dark:text-orange-200 dark:ring-orange-700/50',
  ibkr:      'bg-cyan-50 text-cyan-700 ring-cyan-300 dark:bg-cyan-950/40 dark:text-cyan-200 dark:ring-cyan-700/50',
  etrade:    'bg-violet-50 text-violet-700 ring-violet-300 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-700/50',
  merrill:   'bg-purple-50 text-purple-700 ring-purple-300 dark:bg-purple-950/40 dark:text-purple-200 dark:ring-purple-700/50',
  sofi:      'bg-teal-50 text-teal-700 ring-teal-300 dark:bg-teal-950/40 dark:text-teal-200 dark:ring-teal-700/50',
  public:    'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-300 dark:bg-fuchsia-950/40 dark:text-fuchsia-200 dark:ring-fuchsia-700/50',
  jpm:       'bg-indigo-50 text-indigo-700 ring-indigo-300 dark:bg-indigo-950/40 dark:text-indigo-200 dark:ring-indigo-700/50',
  other:     'bg-zinc-100 text-zinc-700 ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-200 dark:ring-zinc-600',
};
